import { useQuery, useQueryClient } from "react-query"
import _ from "lodash"
import {
  getEvent, getEvents,
  getLeadRetrievalLicenses,
  getLeadScans,
  getPublicEvent,
  getSegmentSizes,
  resolveLeadRetrievalLicenseToken,
  getEventActivity,
  getEventPageViews,
  putEvent,
} from "api/events"
import { matchPath, useLocation } from "react-router-dom"
import { isUUID } from "utilities/strings"
import { errorToast } from "utilities/toasts"
import { EventUserRole } from "services/permissions"
import { currentUserId } from "services/authentication"
import {
  DashboardLayout, DioboxEvent, EventActivityParams, EventPageViewsParams,
} from "../sharedTypes"

export const eventsCacheKey = (
  accountId: string, dateFilter: string|null, cloneId: string|null,
) => ["events", accountId, dateFilter, cloneId]
export const eventCacheKey = (eventId: string) => ["event", eventId]
const publicEventCacheKey = (eventId: string) => ["publicEventCacheKey", eventId]
export const leadRetrievalLicensesCacheKey = (eventId: string) => ["leadRetrievalLicenses", eventId]
export const leadScansCacheKey = (eventId: string, eventAccessId: number) => ["leadScans", eventId, eventAccessId]
export const segmentSizesCacheKey = (eventId: string) => ["segmentSizes", eventId]

export const useCurrentEvent = () => {
  const eventId = (matchPath(useLocation().pathname, "/events/:eventId")?.params as any)?.eventId

  const { isLoading, data: event } = useQuery(
    eventCacheKey(eventId), () => getEvent(eventId).then(({ data }) => {
      // eslint-disable-next-line no-param-reassign
      data.currentUserRole = (data.users || [])
        .find((eventUser) => eventUser.user?.id === currentUserId())?.role as EventUserRole

      return data
    }),
    { enabled: isUUID(eventId) },
  )

  return { isLoading, data: event || {} as DioboxEvent }
}

// Shows a hand tick at once and saves the event's ticks; if saving fails, only this step goes
// back to what it was.
export const useChecklistTick = (eventId: string) => {
  const queryClient = useQueryClient()

  return (step: string, done: boolean | null) => {
    const key = eventCacheKey(eventId)
    const ticks = () => queryClient.getQueryData<DioboxEvent>(key)?.checklistTicks ?? {}
    const setTicks = (checklistTicks: Record<string, boolean>) => queryClient.setQueryData(
      key, (event: DioboxEvent | undefined) => ({ ...event, checklistTicks }) as DioboxEvent,
    )
    const setStep = (value?: boolean | null) => setTicks(
      value === null || value === undefined ? _.omit(ticks(), step) : { ...ticks(), [step]: value },
    )
    const previous = ticks()[step]

    setStep(done)

    return putEvent(eventId, { checklistTicks: ticks() } as DioboxEvent)
      .then(({ data }) => setTicks(data.checklistTicks ?? {}))
      .catch(() => setStep(previous))
  }
}

// Shows the changed phases' dashboard layouts at once and saves every phase's layout on the
// event. null sends a phase back to the default. If saving fails, the previous layouts come back.
export const useSaveDashboardLayout = (eventId: string) => {
  const queryClient = useQueryClient()

  return (changes: Record<string, DashboardLayout | null>) => {
    const key = eventCacheKey(eventId)
    const sent = _.mapKeys(changes, (_layout, phase) => _.camelCase(phase))
    const previous = queryClient.getQueryData<DioboxEvent>(key)?.dashboardLayouts ?? {}
    const setLayouts = (dashboardLayouts: Record<string, DashboardLayout>) => (
      queryClient.setQueryData(
        key, (event: DioboxEvent | undefined) => ({ ...event, dashboardLayouts }) as DioboxEvent,
      )
    )

    const dashboardLayouts = _.omitBy(
      { ...previous, ...sent }, _.isNil,
    ) as Record<string, DashboardLayout>

    setLayouts(dashboardLayouts)

    return putEvent(eventId, { dashboardLayouts } as DioboxEvent)
      .then(({ data }) => data.dashboardLayouts && setLayouts(data.dashboardLayouts))
      .catch(() => {
        setLayouts(previous)
        errorToast({
          title: "Your dashboard layout couldn't be saved.",
          description: "Please try again.",
        })
      })
  }
}

export const useEvents = (
  accountId: string, dateFilter?: string|null, cloneId?: string|null,
) => useQuery(
  eventsCacheKey(accountId, dateFilter || null, cloneId || null),
  () => getEvents(dateFilter || null).then(({ data }) => data),
)

export const usePublicEvent = (eventId: string) => useQuery(publicEventCacheKey(eventId),
  () => getPublicEvent(eventId).then(({ data }) => data))

export const useSegmentSizes = (eventId: string) => {
  const { data } = useQuery(
    segmentSizesCacheKey(eventId),
    () => getSegmentSizes(eventId).then(({ data: sizes }) => sizes),
    { enabled: !!eventId },
  )

  return data
}

export const useLeadRetrievalLicenses = (eventId: string) => useQuery(
  leadRetrievalLicensesCacheKey(eventId),
  () => getLeadRetrievalLicenses(eventId).then(({ data }) => data),
)

export const useLeadScans = (eventId: string, eventAccessId?: number) => useQuery(
  leadScansCacheKey(eventId, eventAccessId ?? -1),
  () => getLeadScans(eventId, eventAccessId as number).then(({ data }) => data),
  { enabled: eventAccessId !== undefined },
)

export const leadRetrievalLicenseTokenCacheKey = (token: string) => ["leadRetrievalLicenseToken", token]

export const useLeadRetrievalLicenseToken = (token: string) => useQuery(
  leadRetrievalLicenseTokenCacheKey(token),
  () => resolveLeadRetrievalLicenseToken(token).then(({ data }) => data),
)

export const eventActivityCacheKey = (eventId: string, params: EventActivityParams) => (
  ["eventActivity", eventId, params]
)

export const useEventActivity = (
  eventId: string,
  params: EventActivityParams,
) => useQuery(
  eventActivityCacheKey(eventId, params),
  () => getEventActivity(eventId, params).then(({ data }) => data),
)

export const eventPageViewsCacheKey = (eventId: string, params: EventPageViewsParams) => (
  ["eventPageViews", eventId, params]
)

export const useEventPageViews = (eventId: string, params: EventPageViewsParams) => useQuery(
  eventPageViewsCacheKey(eventId, params),
  () => getEventPageViews(eventId, params).then(({ data }) => data),
)
